import React from "react";
import { OriginRoute, DestinyRoute, ITripStopItem } from "@types";
import { formatStopArrivalLabel } from "@utils";
import { Button, SimpleTooltip } from "@rio-cloud/rio-uikit";

interface InputItemProps {
  id: string;
  label: string;
  value: string;
  iconLeft: string;
  onCheckpointItem?: () => void;
  onShareItem?: () => void;
  disabled?: boolean;
  showCheckpointButton?: boolean;
}

const Input: React.FC<InputItemProps> = ({ id, label, value, iconLeft, onCheckpointItem, onShareItem, disabled = false, showCheckpointButton = true }) => (
  <div className="display-flex align-items-center margin-top-10 margin-bottom-10">
    <div className="width-100pct">
      <label htmlFor={id}>{label}</label>
      <div className="input-group gap-15">
        <span className="input-group-addon">
          <span className={iconLeft} />
        </span>
        <input id={id} className="form-control" type="text" placeholder="" disabled value={value} />
        {onShareItem && (
          <SimpleTooltip content={<span>Compartilhar parada da viagem</span>} placement="top">
            <Button
              bsStyle={Button.PRIMARY}
              iconOnly
              iconName="rioglyph rioglyph-share-nodes"
              onClick={onShareItem}
            />
          </SimpleTooltip>
        )}
        {showCheckpointButton && (
          <Button
            bsStyle={Button.PRIMARY}
            iconOnly
            iconName="rioglyph rioglyph-checkboxes"
            disabled={disabled}
            onClick={onCheckpointItem}
          />
        )}
      </div>
    </div>
  </div>
);

const buildStopLabel = (stop: ITripStopItem): string => {
  if (stop.actualArrival) return `${stop.note} - Chegada em ${formatStopArrivalLabel(stop.actualArrival)}`;
  if (stop.estimatedArrival) return `${stop.note} - Previsão de chegada ${formatStopArrivalLabel(stop.estimatedArrival)}`;
  if (stop.isUnplanned || !stop.routeStopId) return stop.note;
  return `${stop.note} - Previsão de chegada --`;
};

interface StopsSectionProps {
  origin?: OriginRoute;
  destiny?: DestinyRoute;
  originLabel?: string;
  destinyLabel?: string;
  showCheckpointButton?: boolean;
  stops?: ITripStopItem[];
  onClickCheckpoint?: (id: string) => void;
  onShareStop?: (stop: ITripStopItem) => void;
  showOriginDestiny?: boolean;
}

export const StopsSection: React.FC<StopsSectionProps> = ({
  origin,
  destiny,
  originLabel,
  destinyLabel,
  stops,
  onClickCheckpoint,
  onShareStop,
  showCheckpointButton = true,
  showOriginDestiny = true,
}) => (
  <div className="padding-y-10 padding-x-15">
    {showOriginDestiny && (
      <Input
        id="trip-stop-origin"
        label="Origem"
        value={origin?.address?.label || originLabel || ""}
        iconLeft="rioglyph rioglyph-start"
        disabled
        showCheckpointButton={showCheckpointButton}
      />
    )}
    {stops && stops.length > 0 && (
      <div className={showOriginDestiny ? "padding-left-20" : ""}>
        {stops.map((stop, index) => (
          <Input
            key={stop.routeStopId ?? stop.checkpointId ?? `stop-${index}`}
            id={`trip-stop-${index}`}
            label={buildStopLabel(stop)}
            value={stop.label}
            iconLeft="rioglyph rioglyph-pushpin"
            disabled={!stop.checkpointId}
            onCheckpointItem={() => stop.checkpointId && onClickCheckpoint?.(stop.checkpointId)}
            onShareItem={onShareStop && stop.routeStopId ? () => onShareStop(stop) : undefined}
            showCheckpointButton={showCheckpointButton}
          />
        ))}
      </div>
    )}
    {showOriginDestiny && (
      <Input
        id="trip-stop-destiny"
        label="Destino"
        value={destiny?.address?.label || destinyLabel || ""}
        iconLeft="rioglyph-finish"
        disabled
        showCheckpointButton={showCheckpointButton}
      />
    )}
  </div>
);
